import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import NavBar from './components/NavBar';
import Discover from './components/Discover';
import Journal from './components/Journal';
import ScentProfile from './components/ScentProfile';
import EditProfile from './components/EditProfile';
import JournalEntryPanel from './components/JournalEntryPanel';
import FeedbackBubble from './components/FeedbackBubble';
import MemberCard from './components/MemberCard';
import WelcomeGuide from './components/WelcomeGuide';
import { computeArchetype } from './archetypes';
import ScentdLogo from './components/ScentdLogo';

const API_URL = 'https://7b3t8n3es9.execute-api.us-east-1.amazonaws.com/prod';

// Global 401 interceptor — expired Cognito token clears session and returns to login
axios.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('scentd_session');
      window.location.reload();
    }
    return Promise.reject(err);
  }
);

type Screen = 'login' | 'verify' | 'signup' | 'home';
type NavTab = 'discover' | 'journal' | 'myScent' | 'follow';

interface UserProfile {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  memberNumber?: number;
  createdAt?: string;
  unseededFragsAdded?: number;
  seededContributions?: number;
  firstCircle?: boolean;
  archetypeCounts?: Record<string, number>;
  topFamilies?: Array<{ category: string; pct: number }>;
  scentSignals?: {
    preferredDescriptors?: string[];
    avoidedDescriptors?: string[];
    avoidedFamilies?: string[];
    preferredSeasons?: string[];
  };
}

export interface Fragrance {
  fragranceId: string;
  name: string;
  house: string;
  year?: number;
  description?: string;
  bottleImageUrl?: string;
  bottleImageSource?: string;
  topNotes?: string[];
  middleNotes?: string[];
  baseNotes?: string[];
  notes?: string[];
  loveCt?: number;
  likeCt?: number;
  neutralCt?: number;
  dislikeCt?: number;
  ownedCt?: number;
  wantBottleCt?: number;
  wishlistCt?: number;
}

export interface JournalEntry {
  fragranceId: string;
  fragranceName: string;
  fragranceHouse?: string;
  rating: string;           // derived: always = most recent wear log's rating
  ownershipStatus?: string | null;  // legacy only
  wearLog?: any;            // legacy
  wearLogs?: any[];         // each log has: logId, date, rating, ownership[], timeOfDay, season, impressions, notes
  updatedAt: string;
  createdAt: string;
  userId?: string;
}

export interface Ingredient {
  ingredientId: string;
  name: string;
  category: string;
  description: string;
}

function App() {
  const [screen, setScreen] = useState<Screen>('login');
  const [activeTab, setActiveTab] = useState<NavTab>('discover');
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showMemberCard, setShowMemberCard] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const [showPrivacyBanner, setShowPrivacyBanner] = useState(false);
  const [journalFragrance, setJournalFragrance] = useState<any>(null);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [session, setSession] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [user, setUser] = useState<UserProfile | null>(null);
  const [authToken, setAuthToken] = useState<string>('');

  // Restore session from localStorage on mount (12-hour window)
  useEffect(() => {
    try {
      const saved = localStorage.getItem('scentd_session');
      if (saved) {
        const { token, userData, savedAt } = JSON.parse(saved);
        const twelveHours = 12 * 60 * 60 * 1000;
        if (Date.now() - savedAt < twelveHours && token && userData) {
          setAuthToken(token);
          setUser(userData);
          setScreen('home');
          if (!localStorage.getItem('scentd_privacy_ack')) {
            setShowPrivacyBanner(true);
          }
          // Refresh profile in background if scentSignals is missing (stale cached session)
          if (!userData.scentSignals && userData.email) {
            axios.get(`${API_URL}/users?email=${encodeURIComponent(userData.email)}`, {
              headers: { Authorization: `Bearer ${token}` },
            }).then(res => {
              if (res.data.scentProfile?.scentSignals) {
                const refreshed = { ...userData, scentSignals: res.data.scentProfile.scentSignals };
                setUser(refreshed);
                localStorage.setItem('scentd_session', JSON.stringify({ token, userData: refreshed, savedAt }));
              }
            }).catch(() => {});
          }
        } else {
          localStorage.removeItem('scentd_session');
        }
      }
    } catch {
      localStorage.removeItem('scentd_session');
    }
  }, []);

  // Global shared state — loaded once on login
  const [catalog, setCatalog] = useState<Fragrance[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([]);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);

  const archetypeInfo = useMemo(
    () => computeArchetype(journalEntries, catalog, ingredients),
    [journalEntries, catalog, ingredients]
  );
  const [wishlistIds, setWishlistIds] = useState<Set<string>>(new Set());

  const [signup, setSignup] = useState({
    firstName: '',
    lastName: '',
    zipCode: '',
    birthday: '',
    marketingOptIn: false,
    privacyAccepted: false,
  });

  // Derived — no extra fetch needed
  const journalIds = new Set(journalEntries.map(e => e.fragranceId));

  useEffect(() => {
    if (user?.userId && authToken) {
      fetchCatalog();
      fetchJournal();
      fetchIngredients();
      fetchWishlistIds();
      refreshUser();
    }
  }, [user?.userId, authToken]);

  // Poll for new connection requests every 30 seconds
  useEffect(() => {
    if (!user?.userId || !authToken) return;
    const interval = setInterval(() => { refreshUser(); }, 30000);
    return () => clearInterval(interval);
  }, [user?.userId, authToken]);

  const fetchCatalog = async () => {
    setCatalogLoading(true);
    try {
      const res = await axios.get(`${API_URL}/fragrances`);
      const items = (res.data.items || []).sort((a: Fragrance, b: Fragrance) =>
        a.name.localeCompare(b.name)
      );
      if (items.length > 0) setCatalog(items);
    } catch (e) {
      console.error('Failed to fetch catalog:', e);
    } finally {
      setCatalogLoading(false);
    }
  };

  const fetchJournal = async () => {
    try {
      const res = await axios.get(`${API_URL}/journal`, {
        params: { userId: user?.userId },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setJournalEntries(res.data.items || []);
    } catch (e) {
      console.error('Failed to fetch journal:', e);
    }
  };

  const fetchIngredients = async () => {
    try {
      const res = await axios.get(`${API_URL}/ingredients`);
      const items = (res.data.items || []).sort((a: Ingredient, b: Ingredient) =>
        a.name.localeCompare(b.name)
      );
      setIngredients(items);
    } catch (e) {
      console.error('Failed to fetch ingredients:', e);
    }
  };

  const fetchWishlistIds = async () => {
    try {
      const res = await axios.get(`${API_URL}/wishlist`, {
        params: { userId: user?.userId },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const ids = new Set<string>((res.data.items || []).map((item: any) => item.fragranceId));
      setWishlistIds(ids);
    } catch (e) {
      console.error('Failed to fetch wishlist:', e);
    }
  };

  const handleWishlistToggle = async (fragranceId: string, fragranceName: string, fragranceHouse: string) => {
    if (!user?.userId) return;
    const isWishlisted = wishlistIds.has(fragranceId);

    // Optimistic update
    const newIds = new Set(wishlistIds);
    if (isWishlisted) {
      newIds.delete(fragranceId);
    } else {
      newIds.add(fragranceId);
    }
    setWishlistIds(newIds);

    try {
      if (isWishlisted) {
        await axios.delete(`${API_URL}/wishlist`, {
          data: { userId: user.userId, fragranceId },
          headers: { Authorization: `Bearer ${authToken}` },
        });
      } else {
        await axios.post(`${API_URL}/wishlist`, {
          userId: user.userId,
          fragranceId,
          fragranceName,
          fragranceHouse,
        }, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
      }
    } catch (e) {
      setWishlistIds(wishlistIds);
      console.error('Wishlist toggle failed:', e);
    }
  };

  const handleJournalUpdated = () => {
    if (user?.userId) fetchJournal();
  };

  // Silently refresh user record (connections, etc.)
  const refreshUser = async () => {
    if (!user?.userId || !authToken) return;
    try {
      const res = await axios.get(`${API_URL}/users?userId=${user.userId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setUser(prev => {
        if (!prev) return prev;
        const updated = {
          ...prev,
          connections: res.data.connections || [],
          unseededFragsAdded: res.data.unseededFragsAdded || 0,
          seededContributions: res.data.seededContributions || 0,
          createdAt: res.data.createdAt,
          showLastName: res.data.showLastName,
          archetypeCounts: res.data.archetypeCounts || {},
        };
        // Keep localStorage in sync so contributions survive session restore
        try {
          const saved = localStorage.getItem('scentd_session');
          if (saved) {
            const parsed = JSON.parse(saved);
            parsed.userData = { ...parsed.userData, ...updated };
            localStorage.setItem('scentd_session', JSON.stringify(parsed));
          }
        } catch {}
        return updated;
      });
    } catch (e) { console.error('Failed to refresh user:', e); }
  };

  // Silently compute + save archetype + top families whenever data is ready — no tab visit required
  useEffect(() => {
    if (!user?.userId || !authToken || journalEntries.length === 0 || catalog.length === 0 || ingredients.length === 0) return;

    const computed = computeArchetype(journalEntries, catalog, ingredients);
    if (!computed) return;

    const likedEntries = journalEntries.filter(e => e.rating === 'like' || e.rating === 'love');
    const counts: Record<string, number> = {};
    for (const entry of likedEntries) {
      const frag = catalog.find(f => f.fragranceId === entry.fragranceId);
      if (!frag) continue;
      const allNotes = [...(frag.topNotes || []), ...(frag.middleNotes || []), ...(frag.baseNotes || []), ...(frag.notes || [])];
      for (const note of allNotes) {
        const ing = ingredients.find(i => i.ingredientId === note.toLowerCase().replace(/\s+/g, '-') || i.name.toLowerCase() === note.toLowerCase());
        if (ing?.category) counts[ing.category] = (counts[ing.category] || 0) + 1;
      }
    }
    const total = Object.values(counts).reduce((s, n) => s + n, 0);
    const topFamilies = total === 0 ? [] :
      Object.entries(counts).sort(([, a], [, b]) => b - a).slice(0, 3).map(([cat, count]) => ({ category: cat, pct: Math.round((count / total) * 100) }));

    axios.put(`${API_URL}/users`, {
      userId: user.userId,
      action: 'updateArchetype',
      archetypeName: computed.name,
      archetypeColor: computed.color,
      topFamilies,
      journalCount: journalEntries.length,
    }, { headers: { Authorization: `Bearer ${authToken}` } }).catch(() => {});
  }, [user?.userId, journalEntries.length, catalog.length, ingredients.length]);

  const sendCode = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/auth`, {
        action: 'sendCode',
        identifier: email,
      });
      setSession(res.data.session);
      setScreen('verify');
    } catch (e: any) {
      if (e.response?.status === 404 || e.response?.status === 400) {
        setScreen('signup');
      } else {
        setError(e.response?.data?.message || 'Something went wrong. Please try again.');
      }
    }
    setLoading(false);
  };

  const verifyCode = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/auth`, {
        action: 'verifyCode',
        identifier: email,
        code,
        session,
      });

      const idToken = res.data.tokens?.IdToken;
      if (idToken) {
        setAuthToken(idToken);
      }

      try {
        const profileRes = await axios.get(`${API_URL}/users?email=${encodeURIComponent(email)}`, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        if (profileRes.data.firstName) {
          const userId = profileRes.data.userId;
          let archetypeCounts = {};
          try {
            const userRes = await axios.get(`${API_URL}/users?userId=${userId}`, {
              headers: { Authorization: `Bearer ${idToken}` },
            });
            archetypeCounts = userRes.data.archetypeCounts || {};
          } catch { /* non-blocking */ }
          const userData = {
            userId,
            firstName: profileRes.data.firstName,
            lastName: profileRes.data.lastName,
            email: profileRes.data.email,
            memberNumber: profileRes.data.memberNumber,
            createdAt: profileRes.data.createdAt,
            unseededFragsAdded: profileRes.data.unseededFragsAdded || 0,
            seededContributions: profileRes.data.seededContributions || 0,
            archetypeCounts,
            topFamilies: profileRes.data.topFamilies || [],
            scentSignals: profileRes.data.scentProfile?.scentSignals || null,
            noteSentimentMap: profileRes.data.noteSentimentMap || null,
            privacyAcceptedAt: profileRes.data.privacyAcceptedAt || null,
          };
          setUser(userData);
          localStorage.setItem('scentd_session', JSON.stringify({ token: idToken, userData, savedAt: Date.now() }));

          // Backfill server-side consent for users who already acknowledged in localStorage
          if (localStorage.getItem('scentd_privacy_ack') && !profileRes.data.privacyAcceptedAt) {
            axios.put(`${API_URL}/users`, { action: 'acceptPrivacy', userId }, {
              headers: { Authorization: `Bearer ${idToken}` },
            }).catch(() => {});
          }
        }
      } catch (profileErr) {
        // Profile fetch failed — don't silently proceed to home with no user state
        console.error('Could not fetch profile after login:', profileErr);
        setError('Signed in but could not load your profile. Please try again.');
        setScreen('login');
        setLoading(false);
        return;
      }

      setScreen('home');
      if (!localStorage.getItem('scentd_guided')) {
        setShowGuide(true);
        localStorage.setItem('scentd_guided', '1');
      }
      // Show privacy banner if not yet acknowledged server-side or locally
      if (!localStorage.getItem('scentd_privacy_ack')) {
        setShowPrivacyBanner(true);
      }
    } catch (e: any) {
      setError(e.response?.data?.message || 'Invalid code. Please try again.');
      setCode('');
    }
    setLoading(false);
  };

  const createAccount = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/users`, {
        ...signup,
        email,
      });

      setUser({
        userId: res.data.userId,
        firstName: signup.firstName,
        lastName: signup.lastName,
        email,
      });

      const authRes = await axios.post(`${API_URL}/auth`, {
        action: 'sendCode',
        identifier: email,
      });
      setSession(authRes.data.session);
      setCode('');
      setScreen('verify');

    } catch (e: any) {
      const msg = e.response?.data?.message || e.response?.data?.error || '';
      if (e.response?.status === 409 || e.response?.data?.existingAccount || msg.includes('already exists')) {
        setError('An account with this email already exists. Go back and log in instead.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    }
    setLoading(false);
  };

  const validateBirthday = (value: string) => {
    if (!value) return true;
    const regex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/\d{4}$/;
    return regex.test(value);
  };

  if (screen === 'home') {

    if (showEditProfile) {
      return (
        <div className="min-h-screen bg-cream">
          <NavBar
            activeTab={activeTab}
            onTabChange={(tab) => { setActiveTab(tab); setShowEditProfile(false); }}
            onLogoClick={() => setShowEditProfile(false)}
            onBadgeClick={() => setShowMemberCard(true)}
            userBadge={user ? { firstName: user.firstName, lastInitial: user.lastName?.[0] || '', memberNumber: user.memberNumber || 0, archetypeName: archetypeInfo?.name, archetypeColor: archetypeInfo?.color, pendingRequests: ((user as any).connections || []).filter((c: any) => c.status === 'pending_in').length } : undefined}
          />
          <div className="pb-16 md:pb-0">
            <EditProfile
              userId={user?.userId || ''}
              authToken={authToken}
              currentUser={{ firstName: user?.firstName || '', lastName: user?.lastName || '' }}
              onBack={() => setShowEditProfile(false)}
              onSave={(updated) => {
                setUser(prev => prev ? { ...prev, ...updated } : prev);
                setShowEditProfile(false);
              }}
              onLogout={() => {
                localStorage.removeItem('scentd_session');
                setUser(null);
                setAuthToken('');
                setScreen('login');
                setShowEditProfile(false);
              }}
              onDeleteAccount={() => {
                localStorage.removeItem('scentd_session');
                setUser(null);
                setAuthToken('');
                setScreen('login');
                setShowEditProfile(false);
              }}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-cream">
        {showPrivacyBanner && (
          <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/30 px-4 pb-6">
            <div className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full">
              <p className="font-serif text-lg text-charcoal mb-2">A quick note</p>
              <p className="text-stone text-sm leading-relaxed mb-4">
                We've published our{' '}
                <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="underline text-charcoal">Privacy Policy</a>.
                {' '}It covers what data we collect, how it's used, and your rights — including how to download or delete your data.
              </p>
              <button
                onClick={() => {
                  localStorage.setItem('scentd_privacy_ack', '1');
                  setShowPrivacyBanner(false);
                  if (user?.userId && authToken) {
                    axios.put(`${API_URL}/users`, { action: 'acceptPrivacy', userId: user.userId }, {
                      headers: { Authorization: `Bearer ${authToken}` },
                    }).catch(() => {});
                  }
                }}
                className="w-full bg-charcoal text-cream py-3 text-xs tracking-widest uppercase rounded-xl hover:bg-charcoal-light transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        )}
        <NavBar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          onLogoClick={() => setActiveTab('discover')}
          onBadgeClick={() => setShowMemberCard(true)}
            userBadge={user ? { firstName: user.firstName, lastInitial: user.lastName?.[0] || '', memberNumber: user.memberNumber || 0, archetypeName: archetypeInfo?.name, archetypeColor: archetypeInfo?.color, pendingRequests: ((user as any).connections || []).filter((c: any) => c.status === 'pending_in').length } : undefined}
        />
        <div className="pb-16 md:pb-0">

          {activeTab === 'discover' && (
            <Discover
              userId={user?.userId || ''}
              authToken={authToken}
              catalog={catalog}
              catalogLoading={catalogLoading}
              ingredients={ingredients}
              wishlistIds={wishlistIds}
              journalIds={journalIds}
              journalEntries={journalEntries}
              topFamilies={(user as any)?.topFamilies || []}
              scentSignals={(user as any)?.scentSignals || null}
              noteSentimentMap={(user as any)?.noteSentimentMap || null}
              onWishlistToggle={handleWishlistToggle}
              onJournalSaved={handleJournalUpdated}
              onCatalogUpdated={fetchCatalog}
            />
          )}

          {activeTab === 'journal' && (
            <Journal
              userId={user?.userId || ''}
              authToken={authToken}
              catalog={catalog}
              journalEntries={journalEntries}
              onJournalUpdated={handleJournalUpdated}
            />
          )}

          {(activeTab === 'myScent' || activeTab === 'follow') && (
            <ScentProfile
              userId={user?.userId || ''}
              authToken={authToken}
              catalog={catalog}
              journalEntries={journalEntries}
              ingredients={ingredients}
              onEditProfile={() => setShowEditProfile(true)}
              wishlistIds={wishlistIds}
              onWishlistToggle={handleWishlistToggle}
              onAddToJournal={(fragrance) => setJournalFragrance(fragrance)}
              onCatalogUpdated={fetchCatalog}
              connections={(user as any).connections || []}
              onConnectionsChanged={refreshUser}
              archetypeCounts={(user as any).archetypeCounts || {}}
              activeMainTab={activeTab}
              topFamilies={(user as any).topFamilies || []}
              scentSignals={(user as any).scentSignals || null}
              noteSentimentMap={(user as any).noteSentimentMap || null}
            />
          )}

        </div>

        <FeedbackBubble
          userId={user?.userId || ''}
          authToken={authToken}
          firstName={user?.firstName || ''}
          lastName={user?.lastName || ''}
          email={user?.email || ''}
          memberNumber={user?.memberNumber}
          onShowGuide={() => setShowGuide(true)}
        />

        {showGuide && <WelcomeGuide onClose={() => setShowGuide(false)} />}

        {journalFragrance && (
          <JournalEntryPanel
            fragrance={journalFragrance}
            userId={user?.userId || ''}
            authToken={authToken}
            onClose={() => setJournalFragrance(null)}
            onSaved={() => {
              handleJournalUpdated();
              setJournalFragrance(null);
            }}
          />
        )}

        {showMemberCard && user && (
          <MemberCard
            userId={user.userId}
            authToken={authToken}
            user={{
              firstName: user.firstName,
              lastName: user.lastName,
              memberNumber: user.memberNumber || 0,
              createdAt: user.createdAt,
              unseededFragsAdded: user.unseededFragsAdded || 0,
              seededContributions: user.seededContributions || 0,
              connections: (user as any).connections || [],
            }}
            journalEntries={journalEntries}
            catalog={catalog}
            ingredients={ingredients}
            archetypeName={archetypeInfo?.name}
            archetypeColor={archetypeInfo?.color}
            onClose={() => setShowMemberCard(false)}
            onConnectionsChanged={() => { refreshUser(); }}
          />
        )}
      </div>
    );
  }

  if (screen === 'signup') {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6">
        <div className="w-full max-w-sm">
          <div className="text-center mb-10">
            <h1 className="font-serif text-4xl text-charcoal">Create Account</h1>
            <p className="text-stone-light text-sm mt-2">Let's get you started</p>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-xs tracking-widest uppercase text-charcoal-light mb-2">First Name</label>
              <input type="text" value={signup.firstName} onChange={e => setSignup({ ...signup, firstName: (e.target as HTMLInputElement).value })} placeholder="Jane" className="w-full bg-white border border-cream-dark rounded-xl px-4 py-3 text-charcoal placeholder-stone-light focus:outline-none focus:border-stone transition-colors" />
            </div>
            <div>
              <label className="block text-xs tracking-widest uppercase text-charcoal-light mb-2">Last Name</label>
              <input type="text" value={signup.lastName} onChange={e => setSignup({ ...signup, lastName: (e.target as HTMLInputElement).value })} placeholder="Doe" className="w-full bg-white border border-cream-dark rounded-xl px-4 py-3 text-charcoal placeholder-stone-light focus:outline-none focus:border-stone transition-colors" />
            </div>
            <div>
              <label className="block text-xs tracking-widest uppercase text-charcoal-light mb-2">Zip Code</label>
              <input type="text" value={signup.zipCode} onChange={e => setSignup({ ...signup, zipCode: (e.target as HTMLInputElement).value })} placeholder="10001" className="w-full bg-white border border-cream-dark rounded-xl px-4 py-3 text-charcoal placeholder-stone-light focus:outline-none focus:border-stone transition-colors" />
            </div>
            <div>
              <label className="block text-xs tracking-widest uppercase text-charcoal-light mb-2">Birthday</label>
              <input type="text" value={signup.birthday} onChange={e => setSignup({ ...signup, birthday: (e.target as HTMLInputElement).value })} placeholder="MM/DD/YYYY"
                className={`w-full bg-white border rounded-xl px-4 py-3 text-charcoal placeholder-stone-light focus:outline-none transition-colors ${signup.birthday && !validateBirthday(signup.birthday) ? 'border-red-300' : 'border-cream-dark focus:border-stone'}`} />
              {signup.birthday && !validateBirthday(signup.birthday) && <p className="text-red-400 text-xs mt-1">Please use MM/DD/YYYY format</p>}
              <p className="text-stone-light text-xs mt-1">Enter as MM/DD/YYYY</p>
            </div>
            <div className="flex items-start gap-3 pt-2">
              <input type="checkbox" id="privacyAccept" required checked={signup.privacyAccepted} onChange={e => setSignup({ ...signup, privacyAccepted: (e.target as HTMLInputElement).checked })} className="mt-1" />
              <label htmlFor="privacyAccept" className="text-stone text-sm leading-relaxed cursor-pointer">
                I agree to the <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="underline hover:text-charcoal transition-colors">Privacy Policy</a>
              </label>
            </div>
            <div className="flex items-start gap-3 pt-2">
              <input type="checkbox" id="marketingOptIn" checked={signup.marketingOptIn} onChange={e => setSignup({ ...signup, marketingOptIn: (e.target as HTMLInputElement).checked })} className="mt-1" />
              <label htmlFor="marketingOptIn" className="text-stone text-sm leading-relaxed cursor-pointer">
                I'd love to receive updates about new features and occasional fragrance inspiration from Scentd
              </label>
            </div>
          </div>
          {error && (
            <div className="mt-4">
              <p className="text-red-400 text-sm">{error}</p>
              {error.includes('already exists') && <button onClick={() => { setScreen('login'); setError(''); }} className="text-stone underline text-sm mt-1">Go back to login</button>}
            </div>
          )}
          <button onClick={createAccount} disabled={loading || !signup.firstName || !signup.lastName || !signup.privacyAccepted || (signup.birthday !== '' && !validateBirthday(signup.birthday))} className="w-full mt-8 bg-charcoal text-cream py-4 text-sm tracking-widest uppercase rounded-xl hover:bg-charcoal-light transition-colors disabled:opacity-50">
            {loading ? 'Creating...' : 'Create Account'}
          </button>
          <button onClick={() => { setScreen('login'); setError(''); }} className="w-full mt-3 text-stone text-sm text-center hover:text-charcoal transition-colors">Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <div className="text-center mb-12">
          <div className="flex justify-center mb-5">
            <ScentdLogo size={72} />
          </div>
          <h1 className="font-serif text-5xl text-charcoal tracking-wide mb-3">Scentd</h1>
          <p className="text-stone text-sm leading-relaxed mb-1">Your personal fragrance journal.</p>
          <p className="text-stone-light text-xs leading-relaxed">Discover your scent identity, track what you wear, and explore the world of fine fragrance.</p>
        </div>

        {screen === 'login' && (
          <div>
            <label className="block text-xs tracking-widest uppercase text-charcoal-light mb-3">Email Address</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail((e.target as HTMLInputElement).value)}
              onKeyDown={e => e.key === 'Enter' && sendCode()}
              placeholder="jane@example.com"
              className="w-full bg-white border border-cream-dark rounded-xl px-4 py-4 text-charcoal placeholder-stone-light focus:outline-none focus:border-stone transition-colors"
            />
            {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
            <button onClick={sendCode} disabled={loading || !email} className="w-full mt-6 bg-charcoal text-cream py-4 text-sm tracking-widest uppercase rounded-xl hover:bg-charcoal-light transition-colors disabled:opacity-50">
              {loading ? 'Sending...' : 'Continue'}
            </button>
          </div>
        )}

        {screen === 'verify' && (
          <div>
            <p className="text-stone text-sm mb-2 text-center">We sent a code to {email}</p>
            <p className="text-stone-light text-xs mb-1 text-center">Check your spam folder if you don't see it</p>
            <p className="text-stone-light text-xs mb-6 text-center">You'll stay logged in for 12 hours after verifying.</p>
            <label className="block text-xs tracking-widest uppercase text-charcoal-light mb-3">Verification Code</label>
            <input
              type="number"
              value={code}
              onChange={e => setCode((e.target as HTMLInputElement).value)}
              onKeyDown={e => e.key === 'Enter' && verifyCode()}
              placeholder="000000"
              className="w-full bg-white border border-cream-dark rounded-xl px-4 py-4 text-charcoal placeholder-stone-light focus:outline-none focus:border-stone transition-colors text-center text-2xl tracking-widest"
            />
            {error && (
              <div className="mt-2">
                <p className="text-red-400 text-sm">{error}</p>
                <button onClick={async () => { setError(''); setCode(''); await sendCode(); }} className="text-stone text-xs underline mt-1">Resend a new code</button>
              </div>
            )}
            <button onClick={verifyCode} disabled={loading || !code} className="w-full mt-6 bg-charcoal text-cream py-4 text-sm tracking-widest uppercase rounded-xl hover:bg-charcoal-light transition-colors disabled:opacity-50">
              {loading ? 'Verifying...' : 'Verify'}
            </button>
            <button onClick={() => { setScreen('login'); setError(''); setCode(''); }} className="w-full mt-3 text-stone text-sm text-center hover:text-charcoal transition-colors">Back</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
